<template>
  <article>
    <activity-stage :customRootContainerClass="`${keyboardEnabled ? '' : 'full-height'}`">
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <article slot="content" style="height: 100%">
        <div class="build-a-sentence" ref="buildASentenceDOM">
          <div
            class="dropdowns"
            transition="fade-05"
            v-if="showDropdowns"
            :style="{ width: currentRound?.dropdowns?.length > 3 ? '100%' : '70%' }"
          >
            <div
              class="option-container"
              v-for="dropdown of currentRound.dropdowns"
              :key="dropdown.id"
            >
              <dropdown
                :id="dropdown.id"
                :label="dropdown.name"
                :drop-down-id="`${dropdown.id}`"
                :options="dropdown.answers.map(({ answer }) => answer)"
                :enabled="!activeDropdown || dropdown.id === activeDropdown"
                :on-item-select="onDropdownSelect"
                :on-open="onDropdownOpen"
              >
              </dropdown>
            </div>
          </div>

          <div class="complete-sentence" transition="fade-05" v-if="showSentence">
            <div class="left">
              <p class="hidden-input">{{ sentence }}</p>
              <div class="sentence">
                <blinking-cursor-text :text="sentence" :text-step="0"> </blinking-cursor-text>
              </div>
            </div>
            <div class="right">
              <img alt="" class="sentence-image-monochrome" :src="monochromeImage" />
              <img
                v-show="showColor"
                transition="fade"
                class="sentence-image-color"
                :src="colorImage"
                alt="Sentence image"
              />
            </div>
          </div>
        </div>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TweenMax } from 'gsap';

import AssetLoader from 'mixins/AssetLoader';
import ActivityManager from 'mixins/ActivityManager';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ErrorLogger from 'mixins/ErrorLogger';

import ActivityStage from 'components/activity/ActivityStage.vue';
import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';
import Dropdown from 'components/activity/Dropdown.vue';

export default {
  mixins: [AssetLoader, ActivityManager, ErrorLogger, TypingActivityHelper],

  components: {
    ActivityStage,
    BlinkingCursorText,
    Dropdown
  },

  data() {
    return {
      buildASentenceDOM: null,
      activeDropdown: null,
      showDropdowns: true,
      showSentence: false,
      showColor: false,
      sentence: null,
      monochromeImage: null,
      colorImage: null,
      selectedAnswers: {}
    };
  },

  methods: {
    madeAllSelections() {
      return this.currentRound.dropdowns.length === Object.keys(this.selectedAnswers).length;
    },

    handleNewRound() {
      this.showDropdowns = true;
      this.showColor = false;
      this.showSentence = false;
      this.activeDropdown = null;
      this.selectedAnswers = {};
      this.monochromeImage = null;
      this.colorImage = null;
      this.sentence = null;

      this.hideKeyboard();
      TweenMax.delayedCall(0.5, this.staggerDropdowns);
    },

    onDropdownOpen(id, collapsed) {
      this.collapsed = collapsed;
      this.activeDropdown = id;
    },

    onDropdownSelect({ dropDownId, option }) {
      this.activeDropdown = null;
      this.selectedAnswers[dropDownId] = option;

      const { dropdowns } = this.currentRound;
      const { answers } = dropdowns.find(({ id }) => dropDownId === `${id}`);
      const answer = answers.find(({ answer }) => answer === option);

      if (answer.monochromeImage) this.monochromeImage = answer.monochromeImage.image.src;
      if (answer.colorImage) this.colorImage = answer.colorImage.image.src;

      if (this.madeAllSelections()) {
        this.sentence = `${this.currentRound.dropdowns
          .map(({ id }) => this.selectedAnswers[id])
          .join(' ')}.`;

        TweenMax.delayedCall(1, () => {
          this.showDropdowns = false;
          this.resetCompletedSteps();
          this.overwriteProgressText({
            currentText: this.sentence
          });
          this.rerenderScreenReaderAlert(`Type the sentence - ${this.sentence}`);
          this.showKeyboard({ enable: true });
          TweenMax.delayedCall(1, () => {
            this.setActivityContainerHeight();

            this.showSentence = true;

            this.setInstructions(this.activityData.typeSentenceInstructions);

            this.moveTabPositionToLastToolbarListItem();
          });
        });
      }
    },

    async loadActivity() {
      this.createRounds({
        instructions: this.activityData.buildSentenceInstructions
      });

      await this.loadImages();
    },

    async loadImages() {
      this.rounds.forEach(({ dropdowns }) => {
        dropdowns.forEach(({ answers }) => {
          answers.forEach(({ monochromeImage, colorImage }) => {
            if (monochromeImage) this.queueImage(monochromeImage.image.src);
            if (colorImage) this.queueImage(colorImage.image.src);
          });
        });
      });
      return this.downloadImages();
    },

    staggerDropdowns() {
      const staggerFrom = { opacity: 0, x: -10 };
      const staggerTo = { opacity: 1, x: 0 };
      const kwtDOMElements = document.querySelectorAll('.bas-dropdown');
      TweenMax.staggerFromTo(kwtDOMElements, 0.25, staggerFrom, staggerTo, 0.2);
    },

    startActivity() {
      this.hideKeyboard();
      this.setupNewRound();
      this.errorLoggerStart();

      this.$nextTick(this.setActivityContainerHeight);

      this.$nextTick(() => {
        this.staggerDropdowns();
      });
    },

    handleCorrectKey(attempt) {
      this.sndType();
      this.incrementTypingIndex();

      this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence);

      if (this.isEndOfCurrentSentence) {
        this.showColor = true;
        this.sndBell();
        this.completeStep();
        this.setCurrentStep();

        if (this.isEndOfActivity) {
          TweenMax.delayedCall(1.5, this.completeActivity);
          return;
        }

        this.setInstructions(this.activityData.buildSentenceInstructions);

        TweenMax.delayedCall(1, () => {
          this.$refs.buildASentenceDOM.style.height = '100%';
          this.handleNewRound();
          this.setupNewRound();
        });
      }
    },
    moveTabPositionToLastToolbarListItem() {
      const toolbarButtons = document.querySelectorAll('ul.kwt-toolbar li.show > button');

      if (toolbarButtons.length) {
        const lastButton = toolbarButtons[toolbarButtons.length - 1];
        if (lastButton) lastButton.focus();
        this.$nextTick(() => {
          if (lastButton) lastButton.blur();
        });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import 'src/assets/sass/fade.scss';

.option-container {
  width: 200px;
  position: relative;
}

.text,
.sentence {
  font-size: 28px;
  text-align: left;
  font-family: 'hwtfutura';
}

.complete-sentence {
  display: flex;
  height: 100%;

  .left {
    padding-left: 30px;
    padding-right: 10px;
    flex-basis: 55%;
  }

  .right {
    padding-left: 10px;
    padding-right: 30px;
    flex-basis: 45%;
  }

  .left,
  .right {
    display: flex;
    box-sizing: border-box;
    justify-content: center;
    align-items: center;
  }

  .right {
    position: relative;
  }
}

.sentence-image-monochrome,
.sentence-image-color {
  transform: translate(-50%, -50%);
  position: absolute;
  max-width: 100%;
  z-index: 100;
  height: auto;
  left: 50%;
  top: calc(50% + 20px);
}

.sentence-image-color {
  z-index: 110;
}

.build-a-sentence {
  width: 100%;
  height: 100%;
  position: relative;
  text-align: center;

  .dropdowns {
    align-items: self-end;
    box-sizing: border-box;
    display: flex;
    justify-content: space-evenly;
    left: 50%;
    position: absolute;
    transform: translate(-50%, -50%);
    top: 50%;

    @media (max-width: 690px) {
      top: calc(50% + 70px);
      width: 100% !important;
    }

    .option-container {
      position: relative;
      display: inline-block;

      .bas-dropdown {
        width: 200px;
      }
    }
  }
}
</style>
